import { useState, useEffect, useMemo, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { alertService } from '../../../../shared/services/alertService';
import {
  useBreadcrumb,
  BreadcrumbItem,
} from '../../../../layouts/BreadcrumbContext';
import { useLazyEditCounsellingQuery } from '../../counselling.services';

/* eslint-disable @typescript-eslint/no-explicit-any */

/**
 * Migrated from Angular `SeniorityUploadTypeComponent`
 * (src/app/views/pages/counselling/seniority-upload-type).
 *
 * Selection hub for updating seniority on a counselling. Reads the counselling
 * id from the `path` query param, loads the counselling record for its title,
 * and offers two option cards:
 *   - Seniority Order      (hidden for CounsellingType '3' and '4')
 *   - Excel Upload Seniority
 * Each card routes to its target page, forwarding the same `path` query param.
 *
 * UI mirrors the Angular template: an inline "Update Seniority > <Title>"
 * breadcrumb row followed by centered gradient cards. Reuses the existing
 * `.front-page .card` styling (same gradient the Angular scss used).
 */

const str = (v: any): string =>
  v === null || v === undefined ? '' : String(v);

const SeniorityUploadType = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { setSubItems, clearSubItems } = useBreadcrumb();

  // Angular: activatedRoute.queryParams -> params.path (defaults to '').
  // The seniority filters (subject / panelType / block / ManageId) travel with
  // the counselling id via router navigation state.
  const navState = (location.state ?? {}) as {
    path?: unknown;
    subject?: unknown;
    panelType?: unknown;
    block?: unknown;
    manageId?: unknown;
  };
  const counId = String(navState.path ?? '');
  const seniorityFilters = useMemo(
    () => ({
      subject: navState.subject,
      panelType: navState.panelType,
      block: navState.block,
      manageId: navState.manageId,
    }),
    [navState.subject, navState.panelType, navState.block, navState.manageId]
  );
  const [triggerEditCounselling] = useLazyEditCounsellingQuery();
  const [counsellingDetails, setCounsellingDetails] = useState<any>(null);

  // Angular: getOneCounselling() — load the record for its Title/CounsellingType.
  useEffect(() => {
    let active = true;
    const getOneCounselling = async () => {
      try {
        const res: any = await triggerEditCounselling({
          counsellingId: counId,
        }).unwrap();
        if (!active) return;
        if (res?.dataStatus && (res.result ?? []).length > 0) {
          setCounsellingDetails(res.result[0]);
        } else if (res?.message) {
          alertService.error(res.message);
        }
      } catch {
        if (active) alertService.error('Something went wrong');
      }
    };
    getOneCounselling();
    return () => {
      active = false;
    };
  }, [counId, triggerEditCounselling]);

  // ── Navigation handlers (forward the counselling id as `path`) ──────────────

  const handleSeniorityOrder = useCallback(() => {
    debugger;
    navigate('/counselling/seniority-order', {
      state: { path: counId, ...seniorityFilters },
    });
  }, [navigate, counId, seniorityFilters]);

  const handleExcelUpload = useCallback(() => {
    debugger;
    navigate('/counselling/seniority-excel-upload', {
      state: { path: counId, ...seniorityFilters },
    });
  }, [navigate, counId, seniorityFilters]);

  // Angular: handleCounsellingURL() -> /counselling/seniority (mapped to DEE).
  const handleCounsellingURL = useCallback(() => {
    navigate('/counselling/seniority');
  }, [navigate]);

  // Feed the app-shell breadcrumb (Breadcrumb.tsx renders Home + these):
  //   Update Seniority / <Title>
  useEffect(() => {
    const items: BreadcrumbItem[] = [
      { label: 'Seniority Update', onClick: handleCounsellingURL },
    ];
    if (counsellingDetails?.Title) {
      items.push({ label: counsellingDetails.Title });
    }
    setSubItems(items);
    return () => clearSubItems();
  }, [counsellingDetails, handleCounsellingURL, setSubItems, clearSubItems]);

  // Angular *ngIf: Seniority Order hidden for CounsellingType '3' and '4'.
  const counsellingType = str(counsellingDetails?.CounsellingType);
  const showSeniorityOrder = counsellingType !== '4' && counsellingType !== '3';

  return (
    <div className="front-page main-body-content">
      {/* Breadcrumb handled by the app shell (see the useBreadcrumb effect). */}
      <div className="row justify-content-md-center">
        <div className="col-xl-3 mt-4 col-12" />

        {showSeniorityOrder && (
          <div className="col-xl-3 mt-4 col-12">
            <div
              className="card shadow bg-body rounded mx-2 my-2"
              onClick={handleSeniorityOrder}
            >
              <div className="row">
                <div className="col">Seniority Order</div>
              </div>
            </div>
          </div>
        )}

        <div className="col-xl-3 mt-4 col-12">
          <div
            className="card shadow bg-body rounded mx-2 my-2"
            onClick={handleExcelUpload}
          >
            <div className="row">
              <div className="col">Excel Upload Seniority</div>
            </div>
          </div>
        </div>

        <div className="col-xl-3 mt-4 col-12" />
      </div>
    </div>
  );
};

export default SeniorityUploadType;

/* eslint-enable @typescript-eslint/no-explicit-any */
